import React, { useState, useEffect, useMemo } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { PayPalWrapper as PayPalCheckout } from '../components/PayPalWrapper';
import NewsletterOptIn from '../components/NewsletterOptIn';
import { useAuth } from '../context/AuthContext';
import { isProgramPublic } from '../utils/programVisibility';
import SEOHead from '../components/SEOHead';

const API_URL = "";

// Registration/checkout page — noindexed (transactional, keyed by a session
// id in the query string, no evergreen content value). Self-referencing
// canonical still applies in case a crawler ignores noindex.
const RegisterOpenSessionSEO = () => (
  <SEOHead title="Open Session Registration" canonicalPath="/register-open-session" noindex />
);

export const RegisterOpenSession = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const programId = searchParams.get('session');
  const { user, token } = useAuth();

  const [programs, setPrograms] = useState([]);
  const [loading, setLoading] = useState(true);
  // Server-verified pricing — source of truth
  const [verifiedPricing, setVerifiedPricing] = useState(null);
  const [pricingLoading, setPricingLoading] = useState(false);

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    newsletterOptIn: false
  });
  const [showPayPal, setShowPayPal] = useState(false);

  useEffect(() => {
    const fetchPrograms = async () => {
      try {
        const res = await fetch(`${API_URL}/api/content/programs`);
        if (res.ok) setPrograms(await res.json());
      } catch (err) {
        console.error('Error fetching programs:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchPrograms();
  }, []);

  // Auto-fill from logged-in user
  useEffect(() => {
    if (user) {
      setFormData(prev => ({
        ...prev,
        name: prev.name || `${user.first_name || ''} ${user.last_name || ''}`.trim(),
        email: prev.email || user.email || '',
      }));
    }
  }, [user]);

  const session = useMemo(() => {
    return programs.find(p => p.id === parseInt(programId));
  }, [programs, programId]);

  // Fetch server-verified price whenever session or auth changes
  useEffect(() => {
    if (!programId) return;
    const fetchPrice = async () => {
      setPricingLoading(true);
      try {
        const res = await fetch(`${API_URL}/api/payments/program-price/${programId}`, {
          headers: token ? { 'Authorization': `Bearer ${token}` } : {}
        });
        if (res.ok) setVerifiedPricing(await res.json());
      } catch (err) {
        console.error('Error fetching verified price:', err);
      } finally {
        setPricingLoading(false);
      }
    };
    fetchPrice();
  }, [programId, token]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setShowPayPal(false);
  };

  // Price always from server
  const getPrice = () => verifiedPricing?.price ?? (session?.nonMemberPrice ?? session?.price ?? 0);
  const isMemberPrice = verifiedPricing?.price_tier === 'member';
  const isSinglePrice = verifiedPricing?.price_tier === 'single_price';

  const getSessionItems = () => [{
    name: session.title,
    description: `Open Session - ${session.schedule}`,
    quantity: 1,
    price: getPrice(),
    category: 'open_session'
  }];

  const handleProceedToPayment = () => {
    if (!formData.name || !formData.email) {
      toast.error('Please enter your name and email');
      return;
    }
    setShowPayPal(true);
  };

  const handlePaymentSuccess = () => {
    toast.success('Registration complete!', {
      description: `You're registered for ${session.title}. See you there!`
    });
    setTimeout(() => navigate('/classes/adult'), 2000);
  };

  const handlePaymentError = (error) => {
    toast.error('Payment failed. Please try again.');
    console.error('Payment error:', error);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white py-16">
        <RegisterOpenSessionSEO />
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-stone-600">Loading session details...</p>
        </div>
      </div>
    );
  }

  if (!session || !session.isPayPal) {
    return (
      <div className="min-h-screen bg-white py-16">
        <RegisterOpenSessionSEO />
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-3xl font-serif text-stone-900 mb-4">Session Not Found</h1>
          <Button onClick={() => navigate('/classes/adult')} className="bg-cafa-red hover:bg-cafa-red-900 text-white">
            Back to Classes
          </Button>
        </div>
      </div>
    );
  }

  if (!isProgramPublic(session)) {
    const isDraft = (session.status || 'active') === 'draft';
    return (
      <div className="min-h-screen bg-white py-16" data-testid="session-paused-message">
        <RegisterOpenSessionSEO />
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-3xl font-serif text-stone-900 mb-4">{session.title}</h1>
          <span className="inline-block px-3 py-1 mb-4 bg-amber-100 text-amber-800 text-sm font-medium rounded-full">
            {isDraft ? 'Not Yet Open' : 'Currently Paused'}
          </span>
          <p className="text-stone-600 mb-6">
            {isDraft
              ? 'This session is not open for registration yet. Please check back soon.'
              : 'This session is currently paused and is expected to return at a later date. Online registration is not available.'}
          </p>
          <Button onClick={() => navigate('/classes/adult')} className="bg-cafa-red hover:bg-cafa-red-900 text-white">
            Back to Classes
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white" data-testid="open-session-register-page">
      <RegisterOpenSessionSEO />
      {/* Header */}
      <section className="bg-gradient-to-b from-stone-50 to-white py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Button
            onClick={() => navigate('/classes/adult')}
            variant="outline"
            className="mb-6 border-stone-300 text-stone-700 hover:bg-stone-50"
          >
            ← Back to Classes
          </Button>
          <h1 className="text-4xl font-serif text-stone-900 mb-4">Open Session Registration</h1>
          <p className="text-xl text-stone-600">Drop-in life drawing session</p>
        </div>
      </section>

      <section className="py-12">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
          <Card className="border-stone-200">
            <CardHeader>
              <CardTitle className="text-2xl font-serif">{session.title}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Session Details */}
              <div className="space-y-3 pb-6 border-b border-stone-200">
                <p className="text-stone-700">{session.description}</p>
                <div className="space-y-2 text-sm">
                  <p><strong className="text-stone-700">Schedule:</strong> {session.schedule}</p>
                  <p><strong className="text-stone-700">Duration:</strong> {session.duration}</p>
                </div>
                {/* Show pricing info if tiered */}
                {!isSinglePrice && session.memberPrice != null && (
                  <div className="text-sm pt-1">
                    <span className="text-stone-500">Members: </span>
                    <span className="font-bold text-cafa-red">${session.memberPrice}</span>
                    <span className="text-stone-400 mx-2">·</span>
                    <span className="text-stone-500">Non-Members: </span>
                    <span className="font-bold text-stone-700">${session.nonMemberPrice}</span>
                  </div>
                )}
              </div>

              {/* Contact Info */}
              <div className="space-y-4">
                <div>
                  <Label htmlFor="name" className="text-stone-700">Your Name *</Label>
                  <Input
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="mt-2"
                    placeholder="John Doe"
                    data-testid="session-name"
                  />
                </div>
                <div>
                  <Label htmlFor="email" className="text-stone-700">Email *</Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="mt-2"
                    placeholder="john@example.com"
                    data-testid="session-email"
                  />
                </div>
              </div>

              {/* Newsletter Opt-In */}
              <NewsletterOptIn
                checked={formData.newsletterOptIn}
                onChange={(checked) => setFormData({ ...formData, newsletterOptIn: checked })}
                source="open_session_registration"
              />

              {/* ── Server-Verified Pricing Banner ────────────────────────── */}
              {!isSinglePrice && (
                pricingLoading ? (
                  <div className="rounded-lg border border-stone-200 bg-stone-50 p-4 text-sm text-stone-500 animate-pulse">
                    Verifying pricing eligibility…
                  </div>
                ) : isMemberPrice ? (
                  <div className="rounded-lg border border-green-300 bg-green-50 p-4" data-testid="member-price-banner">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="w-2 h-2 rounded-full bg-green-500 inline-block"></span>
                      <span className="text-sm font-semibold text-green-800">Member Price Applied</span>
                    </div>
                    <p className="text-xs text-green-700">
                      Your active Coppini membership has been verified. You'll be charged the member rate.
                    </p>
                  </div>
                ) : verifiedPricing?.reason === 'inactive_member' ? (
                  <div className="rounded-lg border border-amber-300 bg-amber-50 p-4" data-testid="inactive-member-banner">
                    <p className="text-sm font-semibold text-amber-800 mb-1">
                      Your membership is not currently active.
                    </p>
                    <p className="text-xs text-amber-700 mb-2">
                      Renew to receive member pricing. Non-member price will be charged.
                    </p>
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      className="border-amber-400 text-amber-800 hover:bg-amber-100 text-xs"
                      onClick={() => navigate('/membership')}
                      data-testid="renew-membership-btn"
                    >
                      Renew Membership
                    </Button>
                  </div>
                ) : (
                  <div className="rounded-lg border border-stone-300 bg-stone-50 p-4" data-testid="non-member-price-banner">
                    <p className="text-sm font-semibold text-stone-700 mb-1">
                      Member pricing requires login with an active Coppini membership.
                    </p>
                    <p className="text-xs text-stone-500 mb-2">
                      Non-member price will be charged. Log in to check your member eligibility.
                    </p>
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      className="border-stone-400 text-stone-700 hover:bg-stone-100 text-xs"
                      onClick={() => navigate(`/members/login?redirect=${encodeURIComponent(window.location.pathname + window.location.search)}`)}
                      data-testid="login-for-member-price-btn"
                    >
                      Log in for member pricing
                    </Button>
                  </div>
                )
              )}
              {/* ──────────────────────────────────────────────────────────── */}

              {/* Total */}
              <div className={`rounded-lg p-4 border ${isMemberPrice ? 'bg-green-50 border-green-200' : 'bg-stone-50 border-stone-200'}`}>
                <div className="flex justify-between items-center">
                  <div>
                    <span className="text-lg font-semibold text-stone-900">Session Fee:</span>
                    {isMemberPrice && (
                      <span className="ml-2 text-xs font-semibold px-2 py-0.5 bg-green-100 text-green-800 rounded" data-testid="member-price-label">
                        Member Price
                      </span>
                    )}
                  </div>
                  <span className={`text-3xl font-bold ${isMemberPrice ? 'text-green-700' : 'text-cafa-red'}`} data-testid="session-price-display">
                    ${getPrice()}
                  </span>
                </div>
              </div>

              {/* PayPal or Proceed Button */}
              {showPayPal ? (
                <div className="space-y-4">
                  <PayPalCheckout
                    items={getSessionItems()}
                    payerEmail={formData.email}
                    payerName={formData.name}
                    onSuccess={handlePaymentSuccess}
                    onError={handlePaymentError}
                    onCancel={() => setShowPayPal(false)}
                    authToken={token}
                    registrationData={{
                      type: 'open_session',
                      program_id: programId,
                      program_name: session?.title,
                      schedule: session?.schedule,
                      duration: session?.duration,
                      instructor: session?.instructor,
                      attendee_info: formData,
                      price_paid: getPrice(),
                      price_tier: verifiedPricing?.price_tier,
                      newsletter_opt_in: formData.newsletterOptIn
                    }}
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setShowPayPal(false)}
                    className="w-full"
                  >
                    Back to Edit
                  </Button>
                </div>
              ) : (
                <Button
                  onClick={handleProceedToPayment}
                  className="w-full bg-cafa-red hover:bg-cafa-red-900 text-white text-lg py-6"
                  data-testid="session-submit"
                >
                  Proceed to Payment
                </Button>
              )}

              <p className="text-xs text-stone-500 text-center">
                Payment processed securely via PayPal.
                {!isSinglePrice && (
                  <> Not a member? <a href="/membership" className="text-cafa-red hover:underline">Join now</a> and save on every session.</>
                )}
              </p>
            </CardContent>
          </Card>
        </div>
      </section>
    </div>
  );
};
